/**
 * The Give menu (Transfer): another player's name in the party list opens it over the game. It lists your gear, each
 * item with a Transfer button (a stack asks how many: Transfer 1, a typed number, or All). What you transfer lines up
 * under To send, with about how many of their free gear slots it takes; ✕ takes a line back, Send hands it all over
 * in one `transfer` command, and Cancel sends nothing. The lines live only here, on the client: the server weighs the
 * whole send (engine/systems/transfer.ts) and refuses all of it if it doesn't fit.
 */
import type { Command } from '../shared/protocol.ts';
import type { ItemView, PartyMemberView, PlayerView } from '../shared/view.ts';
import { store } from './store.ts';
import { esc, setHtml, toast } from './ui.ts';

type TransferState = NonNullable<typeof store.transfer>;

/** The menu's controls, and the party list's name buttons that open it. */
const TRANSFER_CONTROLS = '[data-transfer-open],[data-transfer-add],[data-transfer-qty],[data-transfer-remove],[data-transfer-send],[data-transfer-close]';

/** The party row of the character the menu gives to, while you can still give it things (null: the menu closes). */
function recipient(view: PlayerView, st: TransferState): PartyMemberView | null {
  if (!view.sheet || view.sheet.dead || st.to === view.sheet.id) return null;
  const row = view.party.find((p) => p.characterId === st.to && !p.isYou);
  return row?.transfer ? row : null;
}

/** Drops the lines whose item is gone and trims each to its stack (it may have shrunk). */
function prune(view: PlayerView, st: TransferState): void {
  const items = new Map((view.sheet?.items ?? []).map((i) => [i.uid, i]));
  st.lines = st.lines.filter((l) => items.has(l.uid)).map((l) => ({ uid: l.uid, qty: Math.max(1, Math.min(l.qty, items.get(l.uid)!.qty)) }));
  if (st.open && !items.has(st.open)) {
    st.open = null;
    st.typed = '';
  }
}

/** About how many gear slots `qty` of the stack takes: its share of the stack's slots, rounded up. */
function slotsFor(i: ItemView, qty: number): number {
  return i.slots > 0 ? Math.ceil((i.slots * qty) / i.qty) : 0;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Where the item is now: what's held or worn is taken off (or handed over straight from the hand) as it goes. */
function whereLabel(i: ItemView): string {
  if (i.location === 'hand') return i.hand === 'both' ? 'Both hands' : i.hand === 'off' ? 'Off hand' : 'Main hand';
  return i.location === 'belt' ? 'Belt' : i.location === 'armor' ? 'Worn' : 'Pack';
}

/** A gear row's Transfer: one button for a lone item, a chooser for a stack (the same shape as the Drop chooser). */
function transferButtons(st: TransferState, i: ItemView, left: number, why: string): string {
  const off = why ? ` disabled title="${esc(why)}"` : '';
  const uid = esc(i.uid);
  if (left <= 1) return `<button type="button" class="small" data-transfer-add="${uid}:1"${off}>Transfer</button>`;
  if (st.open !== i.uid) return `<button type="button" class="small" data-transfer-qty="${uid}"${off || ' title="Choose how many to transfer"'}>Transfer…</button>`;
  return `<span class="drop-qty"><button type="button" class="small" data-transfer-add="${uid}:1"${off}>Transfer 1</button>`
    + `<input type="number" min="1" max="${left}" step="1" placeholder="1–${left}" data-transfer-input="${uid}" data-keep-focus="transfer" aria-label="How many to transfer">`
    + `<button type="button" class="small" data-transfer-add="${uid}:typed"${off} title="Transfer the number typed (Enter)">Transfer</button>`
    + `<button type="button" class="small" data-transfer-add="${uid}:all"${off}>All ${left}</button>`
    + '<button type="button" class="small ghost" data-transfer-qty title="Cancel (Esc)">✕</button></span>';
}

/**
 * The Give menu's HTML for `store.transfer`, or null when it should close (no character of yours, or the other one
 * left the party list or can't be given things any more). Stale lines are pruned first.
 */
export function transferHtml(view: PlayerView): string | null {
  const st = store.transfer;
  const to = st ? recipient(view, st) : null;
  if (!st || !to?.transfer) return null;
  prune(view, st);
  const name = esc(to.characterName ?? to.playerName);
  const { slots, blocked } = to.transfer;
  const items = view.sheet!.items;
  const byUid = new Map(items.map((i) => [i.uid, i]));
  const staged = new Map(st.lines.map((l) => [l.uid, l.qty]));
  const est = st.lines.reduce((n, l) => n + slotsFor(byUid.get(l.uid)!, l.qty), 0);
  const free = slots ? Math.max(0, slots.max - slots.used) : undefined;
  // Their pack has no room left for anything that takes a slot (dungeon-bound and slotless gear always fits).
  const full = free !== undefined && free - est <= 0;
  const stagedRows = st.lines.map((l) => {
    const i = byUid.get(l.uid)!;
    const s = slotsFor(i, l.qty);
    return `<div class="row"><span class="grow">${esc(i.name)}${l.qty > 1 ? ` ×${l.qty}` : ''} <span class="muted">· ${s ? `about ${plural(s, 'slot')}` : 'no slots'}</span>`
      + `${i.transferBlocked ? ` <span class="bad">${esc(i.transferBlocked)}</span>` : ''}</span>`
      + `<button type="button" class="small ghost" data-transfer-remove="${esc(l.uid)}" title="Take it back">✕</button></div>`;
  });
  const gearRows = items.flatMap((i) => {
    const left = i.qty - (staged.get(i.uid) ?? 0);
    if (left <= 0) return [];
    const why = i.transferBlocked ?? (i.slots > 0 && full ? `${to.characterName ?? to.playerName} has no free gear slots` : '');
    const tag = i.temporary ? ' <span class="tag temp" title="Dungeon-bound: takes no gear slots, and is gone when you leave the dungeon">dungeon-bound</span>' : '';
    return [`<div class="row"><span class="grow">${esc(i.name)}${left > 1 ? ` ×${left}` : ''}${tag} <span class="muted transfer-where">· ${whereLabel(i)}</span></span>${transferButtons(st, i, left, why)}</div>`];
  });
  const estLine = free !== undefined && st.lines.length
    ? `<p class="transfer-est ${est > free ? 'bad' : ''}">About ${plural(est, 'gear slot')} of ${free} free${est > free ? `: more than ${name} has room for` : ''}</p>` : '';
  const sendOff = blocked ? ` disabled title="${esc(blocked)}"` : !st.lines.length ? ' disabled title="Transfer something from your gear first"' : '';
  return `
    <div class="modal-box transfer-box" role="dialog" aria-label="Give to ${name}">
      <h1>Give to ${name}</h1>
      ${slots ? `<p class="muted">${name}'s gear: ${slots.used}/${slots.max} slots · ${free} free</p>` : ''}
      ${blocked ? `<p class="bad">${esc(blocked)}</p>` : ''}
      <h2>To send</h2>
      <div class="transfer-list transfer-staged">${stagedRows.join('') || '<span class="muted">Nothing yet: press Transfer on an item below.</span>'}</div>
      ${estLine}
      <h2>Your gear</h2>
      <div class="transfer-list">${gearRows.join('') || '<span class="muted">You have nothing left to give.</span>'}</div>
      <div class="modal-actions"><button type="button" class="primary" data-transfer-send${sendOff}>Send</button><button type="button" data-transfer-close>Cancel</button></div>
    </div>`;
}

/** Shows or hides the Give menu (main.ts calls it with every view). */
export function renderTransfer(view: PlayerView): void {
  const el = document.getElementById('transfer');
  if (!el) return;
  const html = transferHtml(view);
  if (html === null) {
    store.transfer = null;
    el.classList.add('hidden');
    return setHtml(el, '');
  }
  el.classList.remove('hidden');
  setHtml(el, html, () => {
    // The chooser was rewritten under you (their slots or the blocked line changed): put back what you typed.
    const box = el.querySelector<HTMLInputElement>('[data-transfer-input]');
    if (box && !box.value && store.transfer?.typed) box.value = store.transfer.typed;
  });
}

/** Esc: closes the open Transfer chooser first, then the menu. */
export function escapeTransfer(view: PlayerView): void {
  const st = store.transfer;
  if (st?.open) {
    st.open = null;
    st.typed = '';
  } else store.transfer = null;
  renderTransfer(view);
}

/** Opens or closes a stack's Transfer chooser, its box focused. */
function openChooser(view: PlayerView, st: TransferState, uid: string | null): void {
  st.open = uid;
  st.typed = '';
  renderTransfer(view);
  if (uid) document.querySelector<HTMLInputElement>('[data-transfer-input]')?.focus();
}

/**
 * Lines up some of a stack from its Transfer button or chooser: `how` is "1", "all" (what's left of it) or "typed"
 * (the number in its box, a whole number from 1 to what's left; the engine checks it again).
 */
function addLine(view: PlayerView, st: TransferState, uid: string, how: string): void {
  const item = view.sheet?.items.find((i) => i.uid === uid);
  if (!item) return openChooser(view, st, null);
  const line = st.lines.find((l) => l.uid === uid);
  const left = item.qty - (line?.qty ?? 0);
  if (left <= 0) return;
  let qty = how === 'all' ? left : Number(how);
  if (how === 'typed') {
    const box = document.querySelector<HTMLInputElement>(`[data-transfer-input="${CSS.escape(uid)}"]`);
    qty = Number((box?.value || st.typed).trim());
    if (!Number.isInteger(qty) || qty < 1 || qty > left) return toast(`Type a number from 1 to ${left}`);
  }
  if (!Number.isInteger(qty) || qty < 1) return;
  if (line) line.qty = Math.min(item.qty, line.qty + qty);
  else st.lines.push({ uid, qty: Math.min(item.qty, qty) });
  openChooser(view, st, null);
}

/** Sends everything lined up as one `transfer` and clears the lines; the menu stays open for more (their slots update). */
function sendLines(view: PlayerView, st: TransferState, send: (cmd: Command) => void): void {
  if (!st.lines.length) return;
  send({ type: 'transfer', to: st.to, items: st.lines.map(({ uid, qty }) => ({ uid, qty })) });
  st.lines = [];
  openChooser(view, st, null);
}

/** One click on a Give menu control (or a name in the party list). */
function transferClick(view: PlayerView, el: HTMLElement, target: EventTarget | null, send: (cmd: Command) => void): void {
  const ds = el.dataset;
  if (ds.transferOpen) {
    store.transfer = { to: ds.transferOpen, lines: [], open: null, typed: '' };
    return renderTransfer(view);
  }
  if (ds.transferClose !== undefined) {
    // Clicks inside the dialog box don't close it; the backdrop and Cancel do.
    if (ds.transferClose === 'backdrop' && target !== el) return;
    store.transfer = null;
    return renderTransfer(view);
  }
  const st = store.transfer;
  if (!st) return;
  if (ds.transferQty !== undefined) return openChooser(view, st, ds.transferQty || null);
  if (ds.transferAdd) {
    const cut = ds.transferAdd.lastIndexOf(':');
    return addLine(view, st, ds.transferAdd.slice(0, cut), ds.transferAdd.slice(cut + 1));
  }
  if (ds.transferRemove) {
    st.lines = st.lines.filter((l) => l.uid !== ds.transferRemove);
    return renderTransfer(view);
  }
  if (ds.transferSend !== undefined) return sendLines(view, st, send);
}

/** Wires the Give menu's clicks and its chooser box (Enter lines the typed number up, Esc closes the chooser). Call once. */
export function installTransfer(send: (cmd: Command) => void, doc: Document = document): void {
  doc.addEventListener('click', (e) => {
    const el = (e.target as HTMLElement | null)?.closest?.<HTMLElement>(TRANSFER_CONTROLS);
    if (el && store.view) transferClick(store.view, el, e.target, send);
  });
  doc.addEventListener('input', (e) => {
    const el = e.target;
    if (el instanceof HTMLInputElement && el.dataset.transferInput && store.transfer) store.transfer.typed = el.value;
  });
  doc.addEventListener('keydown', (e) => {
    const el = e.target;
    const st = store.transfer;
    if (!(el instanceof HTMLInputElement) || !el.dataset.transferInput || !store.view || !st) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      addLine(store.view, st, el.dataset.transferInput, 'typed');
    } else if (e.key === 'Escape') openChooser(store.view, st, null);
  });
}
